import { useState } from 'react';
import { Link } from 'react-router-dom';

interface FAQItem {
  q: string;
  a: string;
}

const faqs: FAQItem[] = [
  {
    q: 'What is Litbuy Spreadsheet?',
    a: 'Litbuy Spreadsheet is a modern spreadsheet platform that offers powerful data management, automation, and collaboration features. It is designed to help both beginners and advanced users work more efficiently with data through an intuitive interface, pre-built templates, and smart automation tools that reduce manual work.',
  },
  {
    q: 'Is Litbuy Spreadsheet safe to use?',
    a: 'Yes, Litbuy Spreadsheet follows industry-standard security practices to protect user data. All information is encrypted during transmission, and the platform includes access controls, version history, and regular security updates. However, we always recommend users review the official privacy policy and follow best practices for data security when using any online tool.',
  },
  {
    q: 'How does Litbuy Spreadsheet compare to Excel and Google Sheets?',
    a: 'Litbuy Spreadsheet offers several advantages over traditional spreadsheet tools including built-in automation workflows, a more intuitive template system, and streamlined collaboration features. While Excel excels at complex financial modeling and Google Sheets offers tight Google ecosystem integration, Litbuy Spreadsheet focuses on ease of use and smart automation that helps users get more done with less effort.',
  },
  {
    q: 'Who can benefit from using Litbuy Spreadsheet?',
    a: 'Litbuy Spreadsheet is suitable for a wide range of users including small business owners tracking inventory, freelancers managing project budgets, students organizing research data, and teams collaborating on shared reports. Its user-friendly design makes it accessible to beginners while offering enough depth for experienced spreadsheet users.',
  },
  {
    q: 'Are there templates available for Litbuy Spreadsheet?',
    a: 'Absolutely. Litbuy Spreadsheet provides a rich library of pre-built templates covering use cases such as budgeting, project management, inventory tracking, expense reporting, scheduling, and data analysis. These templates are fully customizable and serve as excellent starting points for various projects.',
  },
  {
    q: 'Where can I learn more about Litbuy Spreadsheet?',
    a: 'You can explore our comprehensive guides and articles on this website covering everything from getting started to advanced tips and comparisons. For official product information and pricing, visit the Litbuy Spreadsheet official website or contact their support team directly.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 px-4 md:px-12 bg-background-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
            FAQ
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-foreground-600 text-sm md:text-base">
            Quick answers to common questions about Litbuy Spreadsheet.
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-background-200/70 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer hover:bg-background-100 transition-colors"
              >
                <span className="font-semibold text-foreground-900 text-sm md:text-base pr-4">
                  {faq.q}
                </span>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-foreground-500 text-lg transition-transform duration-200`}
                  ></i>
                </div>
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openIndex === index ? 'max-h-96 pb-4 md:pb-5 px-4 md:px-5' : 'max-h-0'
                }`}
              >
                <p className="text-foreground-600 text-sm leading-relaxed">
                  {faq.a}
                  {index === 5 && (
                    <span>
                      {' '}
                      Check out our{' '}
                      <Link to="/article/what-is-litbuy-spreadsheet" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
                        complete Litbuy Spreadsheet Guide
                      </Link>{' '}
                      for more detailed information.
                    </span>
                  )}
                  {index === 1 && (
                    <span>
                      {' '}
                      Always read our{' '}
                      <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">
                        Safety Disclaimer
                      </Link>{' '}
                      before using any spreadsheet tool.
                    </span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}